import { useEffect, useRef, useState } from 'react';
import { fullWhen } from '../../lib/format';
import { Icon } from '../../lib/icons';
import { useOverlay } from '../ui';

/**
 * A file from a message, shown over the conversation (Slack's viewer, in Stitch's look): pictures
 * large (tap to see them full size), PDFs page by page, video and audio playing in place, text as
 * text; anything else, what it is and Download. With several files in a message, previous and next
 * (and the arrow keys). Nothing in a file ever runs in the page: PDFs are drawn by pdf.js without
 * scripts, text is shown as text, and everything else is only offered for download.
 */

export interface ViewerFile {
  id: string;
  name: string;
  contentType: string;
  /** Unknown for a text's picture. */
  bytes: number | null;
  /** Where it's fetched from (and downloaded). */
  url: string;
  /** A picture the server checked (its first bytes): shown in the page. */
  picture: boolean;
  /** Its last week under the company's file retention (2936): "Removed in 3 days". */
  left?: string;
}

type Kind = 'picture' | 'pdf' | 'video' | 'audio' | 'text' | 'other';

export function kindOf(f: Pick<ViewerFile, 'contentType' | 'picture'>): Kind {
  if (f.picture) return 'picture';
  if (f.contentType === 'application/pdf') return 'pdf';
  if (f.contentType === 'video/mp4' || f.contentType === 'video/quicktime') return 'video';
  if (f.contentType.startsWith('audio/')) return 'audio';
  if (f.contentType === 'text/plain' || f.contentType === 'text/csv') return 'text';
  return 'other';
}

export function fileSize(n: number): string {
  if (n < 1024) return `${n} bytes`;
  if (n < 1024 * 1024) return `${Math.max(1, Math.round(n / 1024))} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/** "PDF", "Word document", "Spreadsheet"… for the card when there's nothing to show. */
const KIND_WORDS: [RegExp, string][] = [
  [/pdf$/, 'PDF'],
  [/wordprocessingml|msword|opendocument\.text|rtf$/, 'Document'],
  [/spreadsheetml|ms-excel|opendocument\.spreadsheet|csv$/, 'Spreadsheet'],
  [/presentationml|ms-powerpoint|opendocument\.presentation/, 'Slides'],
  [/zip$/, 'Zip file'],
  [/^image\//, 'Picture'],
  [/^audio\//, 'Audio'],
  [/^video\//, 'Video'],
  [/^text\//, 'Text']
];
export function kindWords(contentType: string): string {
  return KIND_WORDS.find(([re]) => re.test(contentType))?.[1] ?? 'File';
}

/** Text shown up to this much (a long log or spreadsheet: download it for the rest). */
const MAX_TEXT = 200_000;
/** PDFs: pages drawn up to this many. */
const MAX_PAGES = 30;

export function FileViewer({ files, start, from, onClose }: { files: ViewerFile[]; start: number; from?: { name: string; at: string }; onClose: () => void }) {
  const [i, setI] = useState(Math.min(Math.max(start, 0), files.length - 1));
  const panel = useOverlay(true, onClose);
  const f = files[i]!;
  const many = files.length > 1;
  const count = files.length;
  useEffect(() => {
    if (count < 2) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') setI((n) => (n - 1 + count) % count);
      if (e.key === 'ArrowRight') setI((n) => (n + 1) % count);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [count]);
  const details = [from ? `${from.name} · ${fullWhen(from.at)}` : null, f.bytes !== null ? fileSize(f.bytes) : null, f.left || null].filter(Boolean).join(' · ');
  return (
    <div className="scrim fview-scrim" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="fview" role="dialog" aria-modal="true" aria-label={f.name} ref={panel} tabIndex={-1} data-private="">
        <header className="fview-head">
          <span className="fview-title">
            <strong>{f.name}</strong>
            {details && <small>{details}</small>}
          </span>
          <a className="fview-btn" href={f.url} download={f.name} aria-label={`Download ${f.name}`} title="Download">
            <Icon name="download" size={18} />
          </a>
          <button type="button" className="fview-btn" aria-label="Close" title="Close" onClick={onClose} data-close="">
            <Icon name="x" size={18} strokeWidth={2.2} />
          </button>
        </header>
        <div className="fview-body" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
          <Shown key={f.id + f.url} f={f} />
          {many && (
            <>
              <button type="button" className="fview-nav prev" aria-label="Previous file" onClick={() => setI((n) => (n - 1 + count) % count)}>
                <Icon name="chevLeft" size={22} strokeWidth={2.2} />
              </button>
              <button type="button" className="fview-nav next" aria-label="Next file" onClick={() => setI((n) => (n + 1) % count)}>
                <Icon name="chevRight" size={22} strokeWidth={2.2} />
              </button>
            </>
          )}
        </div>
        {many && (
          <footer className="fview-foot" aria-live="polite">
            {i + 1} of {count}
          </footer>
        )}
      </div>
    </div>
  );
}

function Shown({ f }: { f: ViewerFile }) {
  const kind = kindOf(f);
  if (kind === 'picture') return <Picture f={f} />;
  if (kind === 'video')
    return (
      <video className="fview-video" src={f.url} controls playsInline preload="metadata">
        <a href={f.url} download={f.name}>
          Download {f.name}
        </a>
      </video>
    );
  if (kind === 'audio')
    return (
      <div className="fview-card">
        <Icon name="volume" size={34} />
        <strong>{f.name}</strong>
        <audio src={f.url} controls preload="metadata" />
      </div>
    );
  if (kind === 'text') return <TextFile f={f} />;
  if (kind === 'pdf') return <PdfFile f={f} />;
  return <Other f={f} note="There’s no preview for this kind of file. Download it to open it." />;
}

function Picture({ f }: { f: ViewerFile }) {
  const [full, setFull] = useState(false);
  return (
    <img
      className={`fview-picture${full ? ' full' : ''}`}
      src={f.url}
      alt={f.name}
      title={full ? 'Fit to the screen' : 'Full size'}
      onClick={() => setFull((x) => !x)}
    />
  );
}

function Other({ f, note }: { f: ViewerFile; note: string }) {
  return (
    <div className="fview-card">
      <Icon name="download" size={34} />
      <strong>{f.name}</strong>
      <span className="fview-muted">
        {kindWords(f.contentType)}
        {f.bytes !== null ? ` · ${fileSize(f.bytes)}` : ''}
      </span>
      <p className="fview-muted">{note}</p>
      <a className="btn primary" href={f.url} download={f.name}>
        <Icon name="download" size={17} strokeWidth={2.2} />
        Download
      </a>
    </div>
  );
}

async function fetched(url: string, signal: AbortSignal): Promise<Response> {
  const r = await fetch(url, { credentials: 'same-origin', signal });
  if (!r.ok) throw new Error(`Couldn’t be loaded (${r.status}).`);
  return r;
}

function TextFile({ f }: { f: ViewerFile }) {
  const [text, setText] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    const ac = new AbortController();
    fetched(f.url, ac.signal)
      .then((r) => r.text())
      .then((t) => setText(t.length > MAX_TEXT ? `${t.slice(0, MAX_TEXT)}\n…` : t))
      .catch(() => !ac.signal.aborted && setFailed(true));
    return () => ac.abort();
  }, [f.url]);
  if (failed) return <Other f={f} note="This file couldn’t be shown here. Download it to open it." />;
  if (text === null) return <p className="fview-muted">Loading…</p>;
  return <pre className="fview-text">{text}</pre>;
}

/** A PDF, page by page, drawn by pdf.js (loaded only when a PDF is opened), with nothing in it run. */
function PdfFile({ f }: { f: ViewerFile }) {
  const box = useRef<HTMLDivElement>(null);
  const [state, setState] = useState<'loading' | 'failed' | { pages: number; shown: number }>('loading');
  useEffect(() => {
    const ac = new AbortController();
    let destroy: (() => Promise<void>) | null = null;
    void (async () => {
      try {
        // The "legacy" build: it carries what older browsers (and Safari) lack, which the default build assumes.
        const [pdfjs, worker] = await Promise.all([import('pdfjs-dist/legacy/build/pdf.mjs'), import('pdfjs-dist/legacy/build/pdf.worker.min.mjs?url')]);
        pdfjs.GlobalWorkerOptions.workerSrc = worker.default;
        const data = new Uint8Array(await (await fetched(f.url, ac.signal)).arrayBuffer());
        if (ac.signal.aborted) return;
        // Only the page itself is drawn: no forms, no scripts, no WebAssembly (the page's security policy allows none).
        const task = pdfjs.getDocument({ data, enableXfa: false, useWasm: false });
        destroy = () => task.destroy();
        const doc = await task.promise;
        if (ac.signal.aborted) return;
        const shown = Math.min(doc.numPages, MAX_PAGES);
        setState({ pages: doc.numPages, shown });
        const width = Math.min(box.current?.clientWidth ?? 800, 900);
        const ratio = Math.min(window.devicePixelRatio || 1, 2);
        for (let n = 1; n <= shown && !ac.signal.aborted; n++) {
          const page = await doc.getPage(n);
          const base = page.getViewport({ scale: 1 });
          const viewport = page.getViewport({ scale: (width / base.width) * ratio });
          const canvas = document.createElement('canvas');
          canvas.width = Math.floor(viewport.width);
          canvas.height = Math.floor(viewport.height);
          canvas.style.width = `${Math.floor(viewport.width / ratio)}px`;
          canvas.setAttribute('aria-label', `Page ${n} of ${doc.numPages}`);
          canvas.setAttribute('role', 'img');
          box.current?.appendChild(canvas);
          await page.render({ canvas, viewport }).promise;
        }
      } catch (err) {
        if (!ac.signal.aborted) {
          console.warn('The PDF couldn’t be drawn', err);
          setState('failed');
        }
      }
    })();
    return () => {
      ac.abort();
      void destroy?.().catch(() => undefined);
    };
  }, [f.url]);
  if (state === 'failed') return <Other f={f} note="This PDF can’t be shown here. Download it to open it." />;
  return (
    <div className="fview-pdf">
      {state === 'loading' && <p className="fview-muted">Loading…</p>}
      <div className="fview-pages" ref={box} />
      {typeof state === 'object' && state.pages > state.shown && (
        <p className="fview-muted">
          Showing the first {state.shown} of {state.pages} pages.{' '}
          <a href={f.url} download={f.name}>
            Download it
          </a>{' '}
          to see them all.
        </p>
      )}
    </div>
  );
}
